import { Component, Inject, OnInit } from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormControl,
  Validators,
  FormGroup,
  FormBuilder,
} from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { NexusComponent } from 'app/core/components/nexus/nexus.component';
import {
  PhoneOTPPayloadModel,
  UserMFASettingModel,
  VerifyOTPModel,
} from 'app/core/models/login.model';
import { NexusFormResultModel } from 'app/core/models/nexus-form-result.model';
import { APPLICATION_ID } from 'app/models/constants';
import { SecurityService } from 'app/services/security.service';
import { environment } from 'environments/environment';
import { Subscription, timer } from 'rxjs';
import { map, takeWhile } from 'rxjs/operators';

@Component({
    selector: 'app-mobile-phone-dialog',
    templateUrl: './mobile-phone-dialog.component.html',
    styleUrls: ['./mobile-phone-dialog.component.scss'],
    standalone: false
})
export class MobilePhoneDialogComponent
  extends NexusComponent
  implements OnInit
{
  private timerSubscription!: Subscription;
  public masked_user_otp_method: string;
  public phone_otp_payload_model: PhoneOTPPayloadModel;
  public otp_ttl_seconds = 800;
  public otp_resend_ttl_seconds = 60;
  otpForm: FormGroup;
  timerValue = 60;
  canResend = false;
  controls = Array.from({ length: 6 }, (_, i) => i);
  public mobile_form_result: NexusFormResultModel = new NexusFormResultModel();
  public mobile_form = new UntypedFormGroup({
    mobile_phone: new UntypedFormControl('', Validators.required),
  });
  show_verify_otp = false;
  constructor(
    public dialogRef: MatDialogRef<MobilePhoneDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private security_service: SecurityService,
    private fb: FormBuilder
  ) {
    super();
  }

  ngOnInit(): void {
    if (this.data.phone) {
      this.mobile_form.get('mobile_phone')?.setValue(this.data.phone);
    }

    if(this.data.user_mfa_setting_model){
     this.otp_ttl_seconds = this.data.user_mfa_setting_model.otp_ttl_seconds;
     this.otp_resend_ttl_seconds = this.data.user_mfa_setting_model.otp_resend_ttl_seconds;
    }

    const controlsConfig: { [key: string]: any } = {};
    for (let i = 0; i < 6; i++) {
      controlsConfig[i] = [
        '',
        [Validators.required, Validators.pattern('\\d')],
      ];
    }
    this.otpForm = this.fb.group(controlsConfig);
  }

  close_mobile_phone_dialog(): void {
    this.dialogRef.close();
  }

  save_mobile_phone(): void {
    this.mobile_form_result.Reset();
    this.SetFormTouched(this.mobile_form);

    if (this.mobile_form.invalid) {
      this.mobile_form_result.Incomplete();
      return;
    }

    const mobile_phone = this.mobile_form.get('mobile_phone')?.value;
    const model: UserMFASettingModel = { ...this.data.user_mfa_setting_model };
    model.phone = mobile_phone;
    model.mfa_method = 'SMS';

    this.StartSpinner();
    this.security_service.SaveSecurityPhone(model).subscribe(
      (data) => {
        this.StopSpinner();
        this.dialogRef.close(data);
      },
      (error) => {
        this.mobile_form_result.Error(error);
        this.StopSpinner();
      }
    );
  }

  onInput(event: Event, index: number): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    // Only allow digits
    if (!/^\d$/.test(value)) {
      input.value = '';
      return;
    }

    // Move to next input if value is valid
    if (value && index < this.controls.length - 1) {
      const nextInput =
        document.querySelectorAll<HTMLInputElement>('.otp-input')[index + 1];
      nextInput?.focus();
    }
  }
  clear_otp() {
    this.otpForm.reset();
  }
  startTimer(): void {
    this.timerSubscription?.unsubscribe(); // Prevent overlap
    this.canResend = false;
    const countdownStart = this.otp_resend_ttl_seconds;
    this.timerValue = countdownStart;

    this.timerSubscription = timer(0, 1000)
      .pipe(
        map((i) => countdownStart - i),
        takeWhile((val) => val >= 0)
      )
      .subscribe((val) => {
        this.timerValue = val;
        this.canResend = val === 0;
      });
  }

  onKeyDown(event: KeyboardEvent, index: number): void {
    const input = event.target as HTMLInputElement;
    if (event.key === 'Backspace' && !input.value && index > 0) {
      const prev = input.previousElementSibling as HTMLInputElement;
      prev?.focus();
    }
  }

  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const pastedData = event.clipboardData?.getData('text') || '';
    const digits = pastedData
      .replace(/\D/g, '')
      .split('')
      .slice(0, this.controls.length);

    digits.forEach((digit, index) => {
      const controlKey = this.controls[index].toString();
      this.otpForm.get(controlKey)?.setValue(digit);
    });
  }
  getOtpCode(): string {
    return this.controls
      .map((i) => this.otpForm.get(i.toString())?.value || '')
      .join('');
  }
  resend_otp_details() {

    this.mobile_form_result.Reset();

    this.SetFormTouched(this.mobile_form);
    if (this.mobile_form.invalid) {
      this.mobile_form_result.Incomplete();
      return;
    }

    this.StartSpinner();
    this.startTimer();
    this.phone_otp_payload_model = new PhoneOTPPayloadModel();
    this.phone_otp_payload_model.phone = this.mobile_form.get('mobile_phone')?.value;
    this.masked_user_otp_method = this.phone_otp_payload_model.phone;
    this.security_service.SendPhoneOTP(this.phone_otp_payload_model).subscribe({
      next: (data) => {
        this.phone_otp_payload_model = data;
       this.StopSpinner();
        this.clear_otp();
        this.show_verify_otp = true;
      },
      error: (err) => {
        this.mobile_form_result.Error(err);
        this.StopSpinner();
      },
    });
  }
  verify_mobile_phone_otp() {
       this.mobile_form_result.Reset();

    this.SetFormTouched(this.otpForm);
    if (this.otpForm.invalid) {
      this.mobile_form_result.Incomplete();
      return;
    }
    this.StartSpinner();
    this.startTimer();
    this.phone_otp_payload_model.otp_code = this.getOtpCode();
    this.security_service.VerifyPhoneOTP(this.phone_otp_payload_model).subscribe({
      next: (data) => {
        this.phone_otp_payload_model = data;
        this.StopSpinner();
        const model: UserMFASettingModel = { ...this.data.user_mfa_setting_model };
        model.phone = this.mobile_form.get('mobile_phone')?.value;
        model.mfa_method = 'SMS';
        this.dialogRef.close(model);
      },
      error: (err) => {
        this.mobile_form_result.Error(err);
        this.StopSpinner();
      },
    });
  }
}
